import { Box, Button, Divider, Stack, Table, TableBody, TableCell, TableRow, Typography, Dialog, DialogContent, DialogTitle, DialogActions, IconButton } from "@mui/material";
import { useState } from "react";
import DateComIntervalo from "../../../../../../../components/DateComIntervalo";
import moment, { Moment } from "moment";
import axiosClient from "../../../../../../../lib/axios";
import { useQuery } from "react-query";
import { IGerarExtratoResponse } from "../../../../../../../types/api/financeiro/gerar-extrato";
import { formatarValorMonetario } from "../../../../../../../helpers/masks";
import LoadingApiQuery from "../../../../../../../components/LoadingApiQuery";
import DisplayNotFoundInfo from "../../../../../../../components/DisplayNotFindInfo";
import PrintIcon from '@mui/icons-material/Print';
import useImprimirArquivo from "../../../../../../../lib/useImprimirArquivo";
import tratativaDeDatas from "../../../../../../../helpers/tratativaDeDatas";
import { verificarStringVazia } from "../../../../../../../helpers/verificadores";

export default function DialogLancamentos({
    open,
    onClose,
    banco
}: DialogLancamentosProps) {

    const [form, setForm] = useState<TForm>({
        periodo: {
            inicial: moment().startOf('month'),
            final: moment()
        }
    })
    const [lancamentos, setLancamentos] = useState<IGerarExtratoResponse | null>(null)

    //queries e mutations
    const { isLoading } = useQuery({
        queryKey: ['gerar-extrato', form],
        queryFn: () => axiosClient.get('/financeiro/caixa-bancos/gerar-extrato', {
            params: {
                codigo_banco: banco.codigo,
                periodo_inicial: form.periodo.inicial.format('YYYY-MM-DD'),
                periodo_final: form.periodo.final.format('YYYY-MM-DD')
            }
        }),
        onSuccess: (data) => {
            setLancamentos(data?.data)
        },
        cacheTime: 0,
        retry: false
    })

    const imprimirArquivo = useImprimirArquivo()

    const handleImprimir = () => {
        imprimirArquivo("financeiro/caixa-bancos/exportar-extrato/?codigo_banco=" + banco.codigo + "&periodo_inicial=" + form.periodo.inicial.format('YYYY-MM-DD') + "&periodo_final=" + form.periodo.final.format('YYYY-MM-DD'))
    }

    //funções manipuladoras
    const handleChangeForm = (key: keyof TForm, value: any) => {
        setForm({ ...form, [key]: value })
    }
    //

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="lg"
        >
            <DialogTitle>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6">Lançamentos</Typography>
                    <IconButton size="medium" onClick={handleImprimir}>
                        <PrintIcon fontSize="inherit" />
                    </IconButton>
                </Stack>
            </DialogTitle>

            <DialogContent>
                <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    spacing={2}
                    sx={{ mt: 1, mb: 2 }}
                >
                    <DateComIntervalo
                        datePickerView={['day', 'month', 'year']}
                        formatString='DD/MM/YYYY'
                        data={form.periodo}
                        onChangeData={(value) => handleChangeForm("periodo", value)}
                    />
                </Stack>

                {
                    isLoading &&
                    <LoadingApiQuery extraParams={{ height: 200, alignItems: 'center' }} />
                }
                {
                    (!isLoading && lancamentos) &&
                    <>
                        <Stack justifyContent="center" alignItems="flex-end">
                            <Stack direction="row" alignItems="flex-end" spacing={1}>
                                <Typography
                                    variant="subtitle1"
                                >
                                    Saldo inicial de
                                </Typography>

                                <Typography variant="h6" fontWeight="bold">
                                    {formatarValorMonetario(lancamentos.saldo_inicial)}
                                </Typography>
                            </Stack>
                            <Typography variant="subtitle2">
                                em {tratativaDeDatas(lancamentos.data_inicial)}
                            </Typography>
                        </Stack>
                        <Box>
                            {
                                lancamentos.extrato.length === 0 &&
                                <DisplayNotFoundInfo
                                    label="Nenhuma movimentação encontrada"
                                />
                            }
                            {
                                lancamentos.extrato.length > 0 && lancamentos.extrato.map((lancamento) => (
                                    <Box key={lancamento.data}>
                                        <Stack my={1}>
                                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                                                <Typography variant="subtitle1" fontWeight="bold">
                                                    {moment(lancamento.data).format('D [de] MMMM [de] YYYY')}
                                                </Typography>
                                            </Stack>
                                            <Divider />
                                        </Stack>
                                        <Table size="small">
                                            <TableBody>
                                                {lancamento.movimentacoes.map((movimentacao, index) => (
                                                    <TableRow
                                                        key={index}
                                                        sx={{ '& td': { borderBottom: "none" } }}
                                                    >
                                                        <TableCell
                                                            sx={{ width: '10%' }}
                                                        >
                                                            {movimentacao.numero_documento}
                                                        </TableCell>

                                                        <TableCell
                                                            sx={{ width: '30%' }}
                                                        >
                                                            {movimentacao.historico}
                                                        </TableCell>

                                                        <TableCell
                                                            sx={{ width: '30%' }}
                                                        >
                                                            {verificarStringVazia(movimentacao.beneficiario)}
                                                        </TableCell>

                                                        <TableCell
                                                            align="right"
                                                            sx={{
                                                                width: '15%',
                                                                pr: 0,
                                                                borderBottom: index === lancamento.movimentacoes.length - 1
                                                                    ? "1px solid rgba(224, 224, 224, 1)"
                                                                    : "none"
                                                            }}
                                                        >
                                                            <Typography
                                                                variant="subtitle2"
                                                                color={movimentacao.natureza === "D" ? "success.main" : "error.main"}
                                                            >
                                                                {movimentacao.natureza === "D" ? "+" : "-"} {formatarValorMonetario(movimentacao.valor)} ({movimentacao.natureza})
                                                            </Typography>
                                                        </TableCell>

                                                        <TableCell
                                                            align="right"
                                                            sx={{
                                                                width: '15%',
                                                                pr: 0,
                                                                borderBottom: index === lancamento.movimentacoes.length - 1
                                                                    ? "1px solid rgba(224, 224, 224, 1)"
                                                                    : "none"
                                                            }}
                                                        >
                                                            <Typography
                                                                variant="subtitle2"
                                                            >{formatarValorMonetario(movimentacao.saldo_final)}
                                                            </Typography>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </Box>
                                ))}
                        </Box>
                    </>
                }
            </DialogContent>

            <DialogActions>
                <Button
                    onClick={onClose}
                >
                    Fechar
                </Button>
            </DialogActions>
        </Dialog>
    )
}

type TForm = {
    periodo: {
        inicial: Moment
        final: Moment
    }
}

interface DialogLancamentosProps {
    open: boolean
    onClose: () => void
    banco: {
        codigo: number
        nome: string
    }
}

